/**
 * Secure IndexedDB Wrapper - Multi-Device Support
 *
 * Database schema supports multiple devices, each with:
 * - Its own wrap key (CryptoKey, non-extractable)
 * - Its own device material (wrapped master seed)
 * - Its own keyring (distributed keys)
 *
 * Devices are keyed by seedKid (which serves as device ID).
 */

const DB_NAME = 'practicemd';
const DB_VERSION = 1;

// Object store names
const STORE_WRAP_KEYS = 'wrapKeys';
const STORE_DEVICES = 'devices';
const STORE_KEYRINGS = 'keyrings';
const STORE_METADATA = 'metadata';

// Metadata keys
const META_CURRENT_DEVICE = 'currentDeviceId';

/**
 * Device material stored in IndexedDB
 */
export interface DeviceMaterial {
  seedKid: string;              // Device ID
  wrappedMasterSeed: string;    // Base64
  wrapIV: string;               // Base64
  sigPk: string;                // Base64
  encPk: string;                // Base64
  sigKid: string;
  encKid: string;
  aesKid: string;
  createdAt: number;
  lastUsedAt: number;
  deviceName?: string;
  nickname?: string;            // First two words of mnemonic
  passwordProtected: boolean;   // Future: true when password added
}


// ============================================================================
// Database Initialization
// ============================================================================

/**
 * Open IndexedDB connection
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Create object stores if they don't exist
      if (!db.objectStoreNames.contains(STORE_WRAP_KEYS)) {
        db.createObjectStore(STORE_WRAP_KEYS); // Key: seedKid, Value: CryptoKey
      }
      if (!db.objectStoreNames.contains(STORE_DEVICES)) {
        db.createObjectStore(STORE_DEVICES); // Key: seedKid, Value: DeviceMaterial
      }
      if (!db.objectStoreNames.contains(STORE_KEYRINGS)) {
        db.createObjectStore(STORE_KEYRINGS); // Key: seedKid, Value: KeyringData
      }
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        db.createObjectStore(STORE_METADATA); // Key: string, Value: any
      }
    };
  });
}

// ============================================================================
// Wrap Key Operations (CryptoKey storage)
// ============================================================================


/**
 * Retrieve a wrap key for a device
 * @param seedKid - Device ID
 * @returns CryptoKey or null if not found
 */
export async function retrieveWrapKey(seedKid: string): Promise<CryptoKey | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_WRAP_KEYS], 'readonly');
    const store = transaction.objectStore(STORE_WRAP_KEYS);
    const request = store.get(seedKid);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}

// ============================================================================
// Device Material Operations
// ============================================================================

/**
 * Retrieve device material
 */
export async function retrieveDeviceMaterial(seedKid: string): Promise<DeviceMaterial | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_DEVICES], 'readonly');
    const store = transaction.objectStore(STORE_DEVICES);
    const request = store.get(seedKid);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}


// ============================================================================
// Metadata Operations (Current Device & Device List)
// ============================================================================

/**
 * Get the current device ID
 */
export async function getCurrentDeviceId(): Promise<string | null> {
  const db = await openDB();

  return new Promise((resolve, reject) => {
    const transaction = db.transaction([STORE_METADATA], 'readonly');
    const store = transaction.objectStore(STORE_METADATA);
    const request = store.get(META_CURRENT_DEVICE);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || null);
  });
}


// ============================================================================
// Device Management
// ============================================================================

/**
 * Clear all data (use with extreme caution!)
 */
export async function clearAllData(): Promise<void> {
  // Close any existing connections first
  try {
    const db = await openDB();
    db.close();
  } catch (err) {
    // Database might not exist yet, that's fine
    console.log('[clearAllData] Database not open, proceeding with deletion');
  }

  // Delete the entire database - simpler and more reliable than clearing stores
  return new Promise((resolve, reject) => {
    console.log('[clearAllData] Starting database deletion...');
    const request = indexedDB.deleteDatabase(DB_NAME);

    // Timeout after 3 seconds - if blocked too long, force resolve
    // The database will still be deleted when possible
    const timeout = setTimeout(() => {
      console.warn('[clearAllData] Database deletion timed out (blocked) - proceeding with logout');
      resolve();
    }, 3000);

    request.onsuccess = () => {
      clearTimeout(timeout);
      console.log('[clearAllData] Successfully deleted practicemd database');
      resolve();
    };

    request.onerror = () => {
      clearTimeout(timeout);
      console.error('[clearAllData] Error deleting database:', request.error);
      reject(request.error);
    };

    request.onblocked = () => {
      // Database deletion is blocked by another connection
      // Wait for it to complete, but timeout will handle if it takes too long
      console.warn('[clearAllData] Database deletion blocked - waiting for other connections to close...');
      // Do NOT resolve here - let onsuccess or timeout handle it
    };
  });
}
